import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useTimezone } from "@/contexts/timezone-context";
import { useSetDisposableOptions } from "@/hooks/use-machines";
import { useUserDirectory } from "@/hooks/use-users";
import type { TimezonePreference } from "@/lib/format";
import { pendingDisposableFrom } from "@/lib/providers";
import type { Machine } from "@/lib/types";

const WINDOW_PRESET_HOURS = [1, 2, 4, 8] as const;
type WindowPreset = "now" | "custom" | (typeof WINDOW_PRESET_HOURS)[number];

/**
 * Right-click the disposable toggle to pick who may take the lease (none = anyone) and from when
 * (nothing = now). Renders no box of its own so the wrapped toggle keeps its layout.
 */
export function DisposableMenu({
  machine,
  currentUserId,
  children,
}: {
  machine: Machine;
  currentUserId: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(
    machine.disposable_allowed_user_ids ?? [],
  );
  const [disposableFrom, setDisposableFrom] = useState<string | null>(() =>
    pendingDisposableFrom(machine, Date.now()),
  );
  // Which "Takeable from" row is ticked — state, not derived: a preset is an offset from the click,
  // so "stored == now + 2 h" stops holding a minute later.
  const [selectedPreset, setSelectedPreset] = useState<WindowPreset>(() =>
    disposableFrom ? "custom" : "now",
  );
  const { data: directory, isPending } = useUserDirectory(open);
  const { formatShortTimestamp } = useTimezone();
  const setOptions = useSetDisposableOptions();

  // Own id is a 400 on the backend; service accounts use API keys and loose
  // acquire is cookie-only, so neither could ever take the lease.
  const candidates = (directory ?? []).filter(
    (user) => user.id !== currentUserId && !user.is_service,
  );

  // Every persist sends the whole state: touching one dimension keeps the other.
  // A window that elapsed while the menu sat open would be a 400, so it drops to "Now".
  const persist = (nextUserIds: number[], nextFrom: string | null) => {
    const effectiveFrom = nextFrom && Date.parse(nextFrom) > Date.now() ? nextFrom : null;
    setSelectedUserIds(nextUserIds);
    setDisposableFrom(effectiveFrom);
    if (!effectiveFrom) setSelectedPreset("now");
    setOptions.mutate({
      id: machine.id,
      allowedUserIds: nextUserIds,
      disposableFrom: effectiveFrom,
      name: machine.name,
    });
  };

  const pickPreset = (preset: WindowPreset, nextFrom: string | null) => {
    setSelectedPreset(preset);
    persist(selectedUserIds, nextFrom);
  };

  const customLabel =
    selectedPreset === "custom" && disposableFrom
      ? `At ${formatShortTimestamp(disposableFrom)} (custom)`
      : "Custom time…";

  return (
    <>
      <ContextMenu
        open={open}
        onOpenChange={(nextOpen) => {
          // Re-seed on open only; while open the local state wins so a burst of
          // toggles doesn't fight the invalidation refetch.
          if (nextOpen) {
            setSelectedUserIds(machine.disposable_allowed_user_ids ?? []);
            const seededFrom = pendingDisposableFrom(machine, Date.now());
            setDisposableFrom(seededFrom);
            setSelectedPreset(seededFrom ? "custom" : "now");
          }
          setOpen(nextOpen);
        }}
      >
        <ContextMenuTrigger className="contents" render={<span />}>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="min-w-52">
          {/* Label must stay inside the group — base-ui's GroupLabel throws when
              rendered without the group context. */}
          <ContextMenuGroup>
            <ContextMenuLabel>Who can take this machine</ContextMenuLabel>
            <ContextMenuCheckboxItem
              checked={selectedUserIds.length === 0}
              onCheckedChange={(checked) => {
                if (checked) persist([], disposableFrom);
              }}
            >
              Anyone
            </ContextMenuCheckboxItem>
            <ContextMenuSeparator />
            {isPending && <ContextMenuItem disabled>Loading users…</ContextMenuItem>}
            {!isPending && candidates.length === 0 && (
              <ContextMenuItem disabled>No other users</ContextMenuItem>
            )}
            {candidates.map((user) => (
              <ContextMenuCheckboxItem
                key={user.id}
                checked={selectedUserIds.includes(user.id)}
                onCheckedChange={(checked) =>
                  persist(
                    checked
                      ? [...selectedUserIds, user.id]
                      : selectedUserIds.filter((userId) => userId !== user.id),
                    disposableFrom,
                  )
                }
              >
                {user.name}
              </ContextMenuCheckboxItem>
            ))}
          </ContextMenuGroup>
          <ContextMenuSeparator />
          <ContextMenuGroup>
            <ContextMenuLabel>Takeable from</ContextMenuLabel>
            <ContextMenuRadioGroup
              value={selectedPreset}
              onValueChange={(value: WindowPreset) => {
                // base-ui fires this for the already-ticked row too, which is what
                // lets a re-click on the custom row reopen its dialog.
                if (value === "custom") {
                  setCustomOpen(true);
                  return;
                }
                if (value === selectedPreset) return;
                if (value === "now") {
                  pickPreset("now", null);
                  return;
                }
                pickPreset(value, new Date(Date.now() + value * 3_600_000).toISOString());
              }}
            >
              <ContextMenuRadioItem value="now">Now</ContextMenuRadioItem>
              {WINDOW_PRESET_HOURS.map((hours) => (
                <ContextMenuRadioItem key={hours} value={hours}>
                  In {hours} hour{hours === 1 ? "" : "s"}
                </ContextMenuRadioItem>
              ))}
              <ContextMenuRadioItem value="custom" closeOnClick>
                {customLabel}
              </ContextMenuRadioItem>
            </ContextMenuRadioGroup>
          </ContextMenuGroup>
        </ContextMenuContent>
      </ContextMenu>
      {/* Sibling of the menu: the item click closes it, the dialog outlives it. */}
      <CustomWindowDialog
        machineName={machine.name}
        open={customOpen}
        onOpenChange={setCustomOpen}
        initialIso={disposableFrom}
        onSet={(iso) => pickPreset("custom", iso)}
      />
    </>
  );
}

// `<input type="datetime-local">` has no timezone: its value is wall-clock
// digits. Read and write them in the viewer's chosen zone (local or UTC).
function inputValueFromMs(ms: number, timezone: TimezonePreference): string {
  const shift = timezone === "utc" ? 0 : new Date(ms).getTimezoneOffset() * 60_000;
  return new Date(ms - shift).toISOString().slice(0, 16);
}

function msFromInputValue(value: string, timezone: TimezonePreference): number {
  return timezone === "utc" ? Date.parse(`${value}Z`) : new Date(value).getTime();
}

function CustomWindowDialog({
  machineName,
  open,
  onOpenChange,
  initialIso,
  onSet,
}: {
  machineName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialIso: string | null;
  onSet: (iso: string) => void;
}) {
  const { timezone } = useTimezone();
  const [value, setValue] = useState("");

  useEffect(() => {
    if (!open) return;
    setValue(
      inputValueFromMs(initialIso ? Date.parse(initialIso) : Date.now() + 3_600_000, timezone),
    );
  }, [open, initialIso, timezone]);

  const chosenMs = value ? msFromInputValue(value, timezone) : Number.NaN;
  const isFuture = !Number.isNaN(chosenMs) && chosenMs > Date.now();

  // Re-check at click time: the dialog doesn't tick, so `isFuture` may be stale.
  const submit = () => {
    if (!(chosenMs > Date.now())) return;
    onSet(new Date(chosenMs).toISOString());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Takeable from</DialogTitle>
          <DialogDescription>
            {machineName} stays yours until this time; from then on anyone allowed may take it
            without asking. Times are {timezone === "utc" ? "UTC" : "your local time"}.
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          type="datetime-local"
          value={value}
          min={inputValueFromMs(Date.now(), timezone)}
          aria-label="Takeable from"
          aria-invalid={!isFuture}
          aria-describedby={isFuture ? undefined : "disposable-from-hint"}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
        />
        {!isFuture && (
          <p id="disposable-from-hint" className="text-xs text-destructive">
            Pick a time in the future.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!isFuture}>
            Set
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
